import { useEffect } from 'react';
import { useBlocker } from 'react-router';
import { useConfirmLeaveDuringUpload } from '@vivli/core/infrastructure/hook';
import { useHasActiveUploads } from '@vivli/shared/infrastructure/hook';

/**
 * App-wide guard (DEV-485) that warns the user before they browse away while a file upload is still
 * in progress. Mounted once inside the router (in PlatformInitWrapper) so it covers every upload
 * location — data-request attachments, chat, study IPD/documents, VM export results, bulk upload,
 * etc. — all of which funnel through `useDataPackage`, which reports activity to
 * `uploadActivityService`.
 *
 * Two paths, because browsers only permit a custom dialog for one of them:
 *  - In-app navigation (switching tabs, moving between platform features, in-app links): intercepted
 *    with `useBlocker`, showing the custom "Continue Upload" / "Cancel Upload" modal.
 *  - Hard unload (refresh, tab/browser close, external URL): only the browser's generic native
 *    prompt is possible, triggered via `beforeunload`. We do not attempt async cancellation during
 *    teardown (unreliable) — the page unloading tears down the in-flight XHRs, which cancels exactly
 *    the not-yet-complete files.
 */
export const UploadNavigationGuard = () => {
    const hasActiveUploads = useHasActiveUploads();
    const confirmLeaveDuringUpload = useConfirmLeaveDuringUpload();

    const blocker = useBlocker(hasActiveUploads);

    // In-app navigation: prompt with the shared confirm, then proceed or cancel the blocked navigation.
    useEffect(() => {
        if (blocker.state !== 'blocked') {
            return;
        }

        confirmLeaveDuringUpload().then((leave) => (leave ? blocker.proceed?.() : blocker.reset?.()));
        // Only react to the transition into the blocked state; blocker.proceed/reset are stable for it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocker.state]);

    // Browser-level unload: generic native prompt while an upload is running.
    useEffect(() => {
        if (!hasActiveUploads) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasActiveUploads]);

    return null;
};
